import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useCourse } from '@/contexts/CourseContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send } from 'lucide-react';

export default function ChatPanelEmbedded({ 
  onSendMessage = null
}) {
  const { chatHistory, sendChatMessage, loading } = useCourse();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Use custom send handler if provided, otherwise use context
  const onSend = onSendMessage || sendChatMessage;
  const isLoading = loading;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [message]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;
    
    // Clear input immediately for better UX
    const messageToSend = message;
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Send the message
    await onSend(messageToSend);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-orange-50 via-white to-teal-50 px-6 pt-0 pb-6 rounded-3xl shadow-2xl flex flex-col" data-testid="chat-panel-embedded">
      <div className="flex items-center gap-2 mb-4 pt-4 flex-shrink-0">
        <MessageCircle className="text-orange-600" size={28} />
        <h2 className="text-2xl font-bold text-gray-800">Coby</h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto mb-4 space-y-4 bg-white rounded-2xl p-5 shadow-lg" data-testid="chat-messages">
        {chatHistory.length === 0 && (
          <div className="text-center text-gray-400 mt-8">
            <p className="text-base">Hello! I'm Coby, your AI assistant. I'm here to help you design your course. Ask me anything!</p>
          </div>
        )}
        {chatHistory.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`chat-message-${index}`}
          >
            <div
              className={`max-w-[80%] p-4 rounded-2xl ${
                msg.role === 'user'
                  ? 'bg-gradient-to-br from-orange-500 to-pink-500 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
            >
              {msg.isLoading ? (
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              ) : (
                <div className="text-base chat-message-text prose prose-base max-w-none prose-p:my-2 prose-p:leading-relaxed prose-headings:mt-3 prose-headings:mb-2 prose-ul:my-2 prose-li:my-1">
                  <ReactMarkdown>{msg.text || msg.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2 items-end flex-shrink-0">
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
            placeholder="Type your message..."
          disabled={isLoading}
          data-testid="chat-input"
            className="flex-1 resize-none overflow-y-auto min-h-[40px] max-h-[150px] border-2 border-orange-200 focus:border-orange-400 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          rows={1}
          style={{ lineHeight: '1.5' }}
        />
        <Button
          onClick={handleSend}
          disabled={isLoading || !message.trim()}
          data-testid="send-chat-button"
          className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed self-end mb-0"
        >
          <Send size={20} />
        </Button>
      </div>
    </div>
  );
}